import {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useId,
  useState,
} from 'react';

import { TooltipComponent } from './TooltipComponent';
import { TooltipController } from './TooltipController';
import {
  type Point,
  type TooltipAlign,
  type TooltipPlacement,
} from './tooltipPlacement';
import { type DetectType, Trigger, type TriggerElementProps } from './Trigger';

type TooltipProps = {
  align?: TooltipAlign;
  autoPlacement?: boolean;
  children: ReactElement<TriggerElementProps>;
  className?: string;
  closeOnClick?: boolean;
  content: ReactNode;
  detect?: DetectType;
  id?: string;
  onOpen?: () => void;
  openOnFocus?: boolean;
  placement?: TooltipPlacement;
  targetMargin?: Point;
  timeOut?: number;
  triggerClose?: boolean;
};

export const Tooltip = memo<TooltipProps>(
  ({
    align,
    autoPlacement = true,
    children,
    className,
    closeOnClick = false,
    content,
    detect = 'hover',
    id,
    onOpen,
    openOnFocus = false,
    placement = 'top',
    targetMargin = { x: 12, y: 12 },
    timeOut,
    triggerClose = false,
  }) => {
    const generatedId = useId();
    const tooltipId = id ?? `tooltip-${generatedId}`;
    const [isOpen, setIsOpen] = useState(false);
    const handleStateChange = useCallback(
      (isOpen: boolean) => {
        setIsOpen(isOpen);
        if (isOpen && onOpen) {
          onOpen();
        }
      },
      [onOpen],
    );

    if (!content) {
      return children;
    }

    return (
      <TooltipController
        align={align}
        autoPlacement={autoPlacement}
        closeOnClick={closeOnClick}
        detect={detect}
        id={tooltipId}
        placement={placement}
        returnState={handleStateChange}
        targetMargin={targetMargin}
        timeOut={timeOut}
        triggerClose={triggerClose}
      >
        <Trigger
          openOnFocus={openOnFocus}
          tooltipId={isOpen ? tooltipId : undefined}
        >
          {children}
        </Trigger>
        <TooltipComponent /*
        // @ts-expect-error // TODO: figure out how to best set default for arrow params */
          arrowParams={{}}
          className={className}
          id={tooltipId}
        >
          {content}
        </TooltipComponent>
      </TooltipController>
    );
  },
);
